/* eslint-disable @typescript-eslint/indent */
/* eslint-disable multiline-ternary */
import { InstantSearch, Configure, Index } from 'react-instantsearch-dom'
import { connectStateResults, connectAutoComplete } from 'react-instantsearch/connectors'
import { Component } from 'preact'
import algoliasearch, { Client, QueryParameters, MultiResponse } from 'algoliasearch'
import globals from '../../../utils/globals'
import AlgoliaSearch from './AlgoliaSearch'
import AlgoliaSearchForControl from './AlgoliaSearchForControl'
import { Hit } from 'js/src/preact-components/algolia/components'
import { SearchBox } from './SearchBox'
import Icon from '../../assets'
import Button from '../../../control-components/ui-components/Button'
import Phrase from '../../../libs/translationsUtils/phrase'
import { common } from '../../common'
import { Credentials, insights, getSearchToken } from '../../../services/algoliaService'

const SearchWithAutoComplete = connectAutoComplete(connectStateResults(AlgoliaSearch))
const SearchWithAutoCompleteForControl = connectAutoComplete(
  connectStateResults(AlgoliaSearchForControl)
)

interface Props {
  isControl?: boolean
  newStyle?: boolean
  searchByContentType?: string[]
  isFederatedSalesforceSearch?: boolean
  isFederatedSkilljarSearch?: boolean
  isFederatedFreshdeskSearch?: boolean
  isUserGuest?: boolean
  searchPlaceholder?: string
  callback?: (hit: Hit) => void
  exclude?: number[]
  submitIconType?: string
  category?: number
  isParentCategory?: boolean
  enableFocusToSearch?: boolean
  callbackOnOutsideClick?: () => void
}

interface State {
  searchById: boolean
  searchByContentType?: string[]
  finalInput: string
  searchClient?: Client
  listIndexes: string[]
  isFiltersOverlay: boolean
  isZendeskFederatedSearch: boolean
  hiddenCategories: number[]
}

interface SearchQuery {
  q: string
  contentType?: string
}

function isKnowledgeBaseOverview() {
  if (typeof window === 'undefined') return

  return window.location.pathname === '/knowledge-base'
}

function isCommunityOverview() {
  if (typeof window === 'undefined') return

  return window.location.pathname === '/community'
}

/**
 * @deprecated - use correct typings from the library instead
 */
interface AlgoliaRequest {
  indexName: string
  query: string
  params: QueryParameters
}

function shouldDisplayFilterButton (): boolean {
  if (typeof window === 'undefined') return false
  return Boolean(
    document.querySelector('[data-preact="ideation-destination/filters/ProductAreaFilter"]') ||
    document.querySelector('[data-preact="ideation-destination/filters/IdeaStatusFilter"]')
  )
}

function getContentType (contentTypes: string[]|undefined, isControl: boolean): string[] | undefined {
  if (isControl) return undefined
  if (contentTypes) return contentTypes
  if (typeof window === 'undefined') return undefined
  if (window.location.pathname.match(/^\/ideas/)) {
    return ['ideation']
  }
  if (window.location.pathname.match(/^\/product-updates/)) {
    return ['productUpdate']
  }
  return undefined
}

class Search extends Component<Props, State> {
  communityId: string
  defaultIndexList: string[]

  constructor(props: Props) {
    super(props)

    this.communityId = globals('communityId')
    this.defaultIndexList = [`${this.communityId}`]
    if (props.isFederatedSalesforceSearch) {
      this.defaultIndexList.push(`${this.communityId}-salesforce`)
    }
    if (props.isFederatedSkilljarSearch) {
      this.defaultIndexList.push(`${this.communityId}-skilljar`)
    }
    if (props.isFederatedFreshdeskSearch) {
      this.defaultIndexList.push(`${this.communityId}-freshdesk`)
    }

    this.state = {
      searchById: false,
      searchByContentType: getContentType(this.props.searchByContentType, this.props.isControl || false),
      finalInput: '',
      searchClient: undefined,
      listIndexes: [],
      isFiltersOverlay: false,
      isZendeskFederatedSearch: false,
      hiddenCategories: []
    }
    this.updateSearch = this.updateSearch.bind(this)
    this.filtersOverlayHandler = this.filtersOverlayHandler.bind(this)
    this.revertFilters = this.revertFilters.bind(this)
    this.callbck = this.callbck.bind(this)
  }

  updateSearch(searchTerm: SearchQuery, next: () => void) {
    this.setState(
      {
        searchById: searchTerm.q.match(/^[0-9]+$/) !== null,
        finalInput: searchTerm.q,
        searchByContentType: searchTerm.contentType
          ? [searchTerm.contentType]
          : getContentType(this.props.searchByContentType, this.props.isControl || false)
      },
      next
    )
  }

  async callbck(data: Credentials) {
    const searchClient = algoliasearch(data.client_id, data.token)
    const basicClient = algoliasearch(data.client_id, data.basicToken)

    this.setState({
      isZendeskFederatedSearch: data.isZendeskFederatedSearchEnabled,
      hiddenCategories: data.hiddenCategories
    })

    if (data.isZendeskFederatedSearchEnabled) {
      this.defaultIndexList.push(`${this.communityId}-zendesk`)
    }

    // do not make a search query when the input is empty
    const originalSearch = searchClient.search.bind(searchClient)
    searchClient.search = async function <T>(
      requests: AlgoliaRequest[],
      _?: (err: Error, res: MultiResponse<T>) => void
    ) {
      if (requests.some((request) => request?.params?.query === '')) {
        return { results: [] }
      }
      return originalSearch(requests)
    }

    let indexes = this.defaultIndexList
    try {
      indexes = (await basicClient.listIndexes()).items
        .map((item: { name: string }) => item.name)
        .filter(
          (i: string) =>
            this.defaultIndexList.includes(i) ||
            (common.isModuleActive('federatedSearchApi') && i === `${this.communityId}-federated`)
        )
    } catch (e) {
      console.log('Listing indices failed, using defaultIndexList')
    }

    this.setState({
      listIndexes: indexes,
      searchClient: searchClient
    })

    insights('init', {
      appId: data.client_id,
      apiKey: data.token
    })
  }

  async componentDidMount() {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    await getSearchToken(this.callbck)
  }

  resetFilters(e: Event) {
    e.stopPropagation()
    e.preventDefault()

    window.dispatchEvent(new CustomEvent('resetFilters'))
  }

  revertFilters(e: Event) {
    e.stopPropagation()
    e.preventDefault()

    window.dispatchEvent(new CustomEvent('revertFilters'))
    this.filtersOverlayHandler(e, false)
  }

  filtersOverlayHandler(e: Event, value: boolean) {
    e.stopPropagation()
    e.preventDefault()

    window.dispatchEvent(
      new CustomEvent('filter', {
        detail: {
          display: value
        }
      })
    )

    if (value) {
      this.setState({
        isFiltersOverlay: value
      })
    } else {
      /*
        Items that should be in the filters are added via Portal component
        having setTimeout here is making state change async and give time to filter component to unmount
      */
      window.setTimeout(() => {
        this.setState({
          isFiltersOverlay: !this.state.isFiltersOverlay
        })
      }, 0)
    }

    const bodyEl = document.querySelector('body')

    if (bodyEl) {
      if (bodyEl.style.overflow === 'hidden') {
        bodyEl.style.overflow = 'visible'
      } else {
        bodyEl.style.overflow = 'hidden'
      }
    }
  }

  render() {
    const { props, state } = this
    if (!state.searchClient) {
      return (
        <SearchBox
          submitIconType={props.submitIconType ?? ''}
          enableFocusToSearch={props.enableFocusToSearch}
        />
      )
    }

    return (
      <div className='search-and-filter-wrapper'>
        <PureSearch
          {...props}
          searchClient={state.searchClient}
          updateSearch={this.updateSearch}
          searchById={state.searchById}
          searchByContentType={state.searchByContentType}
          finalInput={state.finalInput}
          categoryId={props.category}
          communityId={this.communityId}
          submitIconType={props.submitIconType ?? ''}
          listIndexes={state.listIndexes}
          isZendeskFederatedSearch={state.isZendeskFederatedSearch}
          hiddenCategories={state.hiddenCategories}
        />
        {shouldDisplayFilterButton() && (
          <button
            className='instant-search__filter'
            onClick={(e: Event) => this.filtersOverlayHandler(e, true)}
          >
            <Icon width={18} height={12} color='#6B707E' icon='filter' />
          </button>
        )}
        {state.isFiltersOverlay && (
          <div
            className={`instant-search__results-wrapper is-hidden-L ${
              !state.isFiltersOverlay ? '' : ''
            }`}
          >
            <div
              className={`instant-search__results-wrapper-filter ${
                !state.isFiltersOverlay ? 'instant-search__results-wrapper-filter--hide' : ''
              }`}
            >
              <div className='instant-search__results-wrapper-filter-header'>
                <Icon icon='close' onClick={(e) => this.revertFilters(e)} />
                <h3 className='instant-search__results-wrapper-filter-header-heading'>
                  <Phrase p='advanced.search.filters' />
                </h3>
                <button
                  onClick={(e) => this.resetFilters(e)}
                  className='instant-search__results-wrapper-filter-header-btn'
                >
                  <Phrase m='Common' p='advanced.search.filter.clear.all' />
                </button>
              </div>
              {/*
               * Filters can be added via Portal component from real place of filters.
               * Since Search does not include them it does not care about which items needs to be display in which order,
               * all those decisions can be done on filter component
               * */}
              <div className='instant-search__results-wrapper-filters' />
              <div className='instant-search__results-wrapper-filter-show-more-wrapper'>
                <Button
                  cta
                  className='instant-search__results-wrapper-filter-show-more'
                  onClick={(event) => this.filtersOverlayHandler(event, false)}
                >
                  <Phrase p='show.results' />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }
}

interface PureProps extends Props {
  communityId: string
  searchById: boolean
  searchByContentType?: string[]
  finalInput: string
  // @ts-expect-error
  searchClient: algoliasearch
  updateSearch: (value: SearchQuery, cb: () => void) => void
  categoryId?: number
  groupId?: number
  listIndexes: string[]
  isZendeskFederatedSearch: boolean
  enableFocusToSearch?: boolean
  callbackOnOutsideClick?: () => void
  hiddenCategories: number[]
}

type SubsetOfPureProps = Pick<
  PureProps,
  | 'isControl'
  | 'categoryId'
  | 'isParentCategory'
  | 'searchByContentType'
  | 'groupId'
  | 'hiddenCategories'
>

export const getFilters = (props: SubsetOfPureProps) => {
  if (!props.isControl && isKnowledgeBaseOverview()) {
    return 'source:knowledge_base'
  }

  if (!props.isControl && isCommunityOverview()) {
    return 'source:community AND (content_type:discussion OR content_type:article OR content_type:question)'
  }

  if (props.categoryId) {
    if (props.isParentCategory) {
      return `category:${props.categoryId}`
    } else {
      return `forum:${props.categoryId}`
    }
  } else if (props.groupId) {
    return `forum:${props.groupId}`
  }

  if (props.searchByContentType) {
    return props.searchByContentType
      .map((value) => {
        return `content_type:${value}`
      })
      .join(' OR ')
  }
  if (props.hiddenCategories && !props.isControl) {
    return props.hiddenCategories
      .map((value) => {
        return `NOT forum:${value}`
      })
      .join(' AND ')
  }

  return undefined
}

const PureSearch = (props: PureProps) => (
  <InstantSearch searchClient={props.searchClient} indexName={props.communityId}>
    {props.listIndexes.includes(props.communityId) && (
      <Index indexName={props.communityId}>
        <Configure
          hitsPerPage={5}
          filters={getFilters(props)}
          queryType={props.searchById ? 'prefixNone' : 'prefixLast'}
          clickAnalytics
          analyticsTags={props.isControl ? ['control'] : ['destination', 'live']}
          restrictSearchableAttributes={
            props.searchById
              ? ['id', 'private_id']
              : ['title', 'first_post', 'answer', 'reply', 'tags', 'forum_title', 'category_title']
          }
        />
      </Index>
    )}

    {props.isFederatedSalesforceSearch &&
      props.listIndexes.includes(`${props.communityId}-salesforce`) && (
        <Index indexName={`${props.communityId}-salesforce`}>
          <Configure
            hitsPerPage={3}
            filters={getFilters(props)}
            clickAnalytics
            analyticsTags={props.isControl ? ['control'] : ['destination', 'live']}
          />
        </Index>
      )}

    {props.isFederatedSkilljarSearch &&
      props.listIndexes.includes(`${props.communityId}-skilljar`) && (
        <Index indexName={`${props.communityId}-skilljar`}>
          <Configure hitsPerPage={3} clickAnalytics />
        </Index>
      )}

    {props.isFederatedFreshdeskSearch &&
      props.listIndexes.includes(`${props.communityId}-freshdesk`) && (
        <Index indexName={`${props.communityId}-freshdesk`}>
          <Configure hitsPerPage={3} clickAnalytics />
        </Index>
      )}

    {props.isZendeskFederatedSearch && props.listIndexes.includes(`${props.communityId}-zendesk`) && (
      <Index indexName={`${props.communityId}-zendesk`}>
        <Configure hitsPerPage={3} clickAnalytics />
      </Index>
    )}

    {props.listIndexes.includes(`${props.communityId}-federated`) && (
      <Index indexName={`${props.communityId}-federated`}>
        <Configure hitsPerPage={20} clickAnalytics />
      </Index>
    )}

    {props.isControl ? (
      <SearchWithAutoCompleteForControl
        updateSearch={(value: SearchQuery, next: () => void) => props.updateSearch(value, next)}
        searchById={props.searchById}
        finalInput={props.finalInput}
        placeholder={props.searchPlaceholder}
        callback={props.callback}
        exclude={props.exclude}
        newStyle={!!props.newStyle}
        submitIconType={props.submitIconType ?? ''}
      />
    ) : (
      <SearchWithAutoComplete
        updateSearch={(value: SearchQuery, next: () => void) => props.updateSearch(value, next)}
        callback={props.callback}
        indexName={props.communityId}
        enableFocusToSearch={props.enableFocusToSearch}
        callbackOnOutsideClick={props.callbackOnOutsideClick}
      />
    )}
  </InstantSearch>
)

export { PureSearch, SearchQuery }

export default Search
